<template lang="pug">
.app-home
    .app-home-header
        .app-home-header-text
            h1 {{ $t('Willkommen') }}
            p(v-if="userName") {{ userName }}
        img.app-home-logo(v-if="logoUrl" :src="logoUrl" alt="Logo")

    el-card.app-tile(
        v-for="app in apps"
        :key="app.name"
        shadow="hover"
        @click.native="$router.push({ name: app.route })"
    )
        .app-tile-inner
            .app-tile-icon
                i(:class="app.icon")
            .app-tile-label {{ app.label }}
            i.el-icon-arrow-right.app-tile-arrow

    .app-home-empty(v-if="!apps.length")
        .app-home-empty-icon
            i.el-icon-fa-inbox.fas
        .app-home-empty-title {{ $t('Keine App-Funktionen verfügbar') }}
        p.app-home-empty-text {{ $t('Für diesen Account sind aktuell keine App-Funktionen freigeschaltet.') }}
</template>

<script>
import _ from 'lodash'
import { useAuthStore, useSetupStore } from '@/stores'
import AuthApi from '@/store/api/system/auth'

export default {
    name: 'AppHome',
    computed: {
        userName() {
            const user = useAuthStore().user
            return [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || ''
        },
        // Firmenlogo aus den CMS-Bildern (type=cms-logo, wie unter /settings/basic).
        // URL wie in AbonementBasic.vue: private Storage-Route + Token.
        logoUrl() {
            const images = _.get(useSetupStore().activeCms, 'images') || []
            const logo = images.find(img => img.type === 'cms-logo' && !img.deleted && !img.disabled)
            if (!logo) return ''
            const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`, '/')
            const table = logo.upload ? 'uploads' : 'images'
            const token = AuthApi.getToken()
            return `${storageRoute}/${table}/${logo.id}/${logo.name}?token=${token}`
        },
        // Events-Scanner nur bei freigeschalteter Event-Manager-Extension –
        // gleiche Bedingung wie die Route-Restriction `extensions: ['event-manager']`.
        hasEvents() {
            return (useSetupStore().enabledExtensions || []).includes('event-manager')
        },
        // Katalog (Produktbilder) nur bei freigeschalteter Shop-Extension –
        // gleiche Bedingung wie die Route-Restriction `extensions: ['shop']`.
        hasCatalog() {
            return (useSetupStore().activeExtensions || []).includes('shop')
        },
        apps() {
            const apps = []

            if (this.hasEvents) {
                apps.push({
                    name: 'events-qr',
                    route: 'scan-events',
                    label: this.$t('Events - QR-Code Scanner'),
                    icon: 'el-icon-fa-qrcode fas',
                })
            }

            if (this.hasCatalog) {
                apps.push({
                    name: 'catalog-images',
                    route: 'scan-products',
                    label: this.$t('Katalog Produktbilder'),
                    icon: 'el-icon-fa-camera fas',
                })
            }

            return apps
        },
    },
}
</script>

<i18n>
{
  "de": {
    "Willkommen": "Willkommen",
    "Events - QR-Code Scanner": "Events - QR-Code Scanner",
    "Katalog Produktbilder": "Katalog - Produktbilder",
    "Keine App-Funktionen verfügbar": "Keine App-Funktionen verfügbar",
    "Für diesen Account sind aktuell keine App-Funktionen freigeschaltet.": "Für diesen Account sind aktuell keine App-Funktionen freigeschaltet."
  }
}
</i18n>

<style lang="sass" scoped>
.app-home
    flex: 1
    min-height: 0
    overflow-y: auto
    width: 100%
    padding: 24px 16px
    max-width: 480px
    margin: 0 auto

.app-home-header
    margin-bottom: 24px
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px

    .app-home-header-text
        min-width: 0

    .app-home-logo
        flex-shrink: 0
        max-width: 96px
        max-height: 36px
        width: auto
        height: auto
        object-fit: contain

    h1
        font-size: 22px
        font-weight: 700
        color: #d0d0d0
        margin: 0 0 4px

    p
        font-size: 14px
        color: #666
        margin: 0

.app-tile
    margin-bottom: 12px
    border-radius: 10px
    cursor: pointer
    -webkit-tap-highlight-color: transparent

    ::v-deep
        .el-card__body
            padding: 0
        &.el-card
            background: #1a1a1a
            border-color: #2a2a2a
            &:hover
                border-color: $--color-primary

    .app-tile-inner
        display: flex
        align-items: center
        gap: 16px
        padding: 18px 16px

    .app-tile-icon
        width: 48px
        height: 48px
        border-radius: 10px
        background: rgba(64, 158, 255, 0.15)
        display: flex
        align-items: center
        justify-content: center
        flex-shrink: 0
        i
            font-size: 22px
            color: $--color-primary

    .app-tile-label
        flex: 1
        font-size: 16px
        font-weight: 500
        color: #d0d0d0

    .app-tile-arrow
        color: #444
        font-size: 14px

.app-home-empty
    display: flex
    flex-direction: column
    align-items: center
    text-align: center
    padding: 48px 28px
    background: #1a1a1a
    border: 1px solid #2a2a2a
    border-radius: 10px

    .app-home-empty-icon
        width: 64px
        height: 64px
        border-radius: 50%
        background: rgba(255, 255, 255, 0.04)
        display: flex
        align-items: center
        justify-content: center
        margin-bottom: 18px

        i
            font-size: 26px
            color: #555

    .app-home-empty-title
        font-size: 16px
        font-weight: 600
        color: #b0b0b0
        margin-bottom: 6px

    .app-home-empty-text
        font-size: 14px
        line-height: 1.4
        color: #666
        margin: 0
        max-width: 280px
</style>
